import { observable, computed, action } from 'mobx';
import {
    IOrderHistory,
    IOrderListApiResponse,
    IOrderGetApiResponse,
    EOrderStatusChangeStatus
} from '../../../common/interfaces/order';
import { user } from './AllStores';
import { app } from '../App';
import { unformat } from 'accounting';

export class Orders {
    public ordersPerPageCount: number = 10;

    @observable public showOrdersHistoryModal: boolean = false;

    @observable public loading: boolean = false;

    @observable public ordersHistory: IOrderHistory[] = [];
    @observable public ordersHistoryTotal: number = 0;
    @observable public ordersHistoryPage: number = 1;

    @computed
    private get ordersHistoryIds(): number[] {
        return this.ordersHistory.map(order => order.id);
    }

    @observable public showOpenStatusPickerForOrderId: number[] = [];
    @observable public ordersStatusChangeStatuses: Array<{ orderId: number; status: EOrderStatusChangeStatus }> = [];

    @computed
    public get ordersHavingStatusChangedIds(): number[] {
        return this.ordersStatusChangeStatuses.map(order => order.orderId);
    }

    @action
    public changeOrdersHistoryPage = (page: number = 1) => {
        this.ordersHistoryPage = page > 0 ? page : 1;
        this.fetchOrdersHistory(page <= 1);
    };

    @action
    public fetchOrdersHistory = async (fromFirstPage: boolean = false): Promise<boolean> => {
        try {
            this.loading = true;

            if (fromFirstPage) {
                this.ordersHistory = [];
                this.ordersHistoryTotal = 0;
                this.ordersHistoryPage = 1;
            }

            const service = await app.service('order');
            const orders = (await service.find({
                query: {
                    cardcode: user.customer.cardcode,
                    $limit: this.ordersPerPageCount,
                    $skip: this.ordersHistoryPage * this.ordersPerPageCount - this.ordersPerPageCount,
                    $sort: {
                        createdAt: -1
                    }
                }
            })) as IOrderListApiResponse;

            this.loading = false;
            this.showOpenStatusPickerForOrderId = [];
            this.ordersHistoryTotal = orders.total;
            this.ordersHistory = orders.data.map(order => ({
                id: order.id,
                cardcode: order.cardcode,
                cardname: order.cardname,
                poNumber: order.poNumber,
                cartId: order.cartId,
                total: order.total,
                orderItemCount: order.orderItemCount,
                status: order.status,
                statusName: order.statusName,
                itemsLoading: false,
                itemsExpanded: false,
                itemsEntries: [],
                createdAt: order.createdAt,
                updatedAt: order.updatedAt
            }));

            return true;
        } catch (error) {
            console.error(error);
            return error;
        }
    };

    @action
    public fetchOrderItems = async (orderId: number = 0, toggle: boolean = true): Promise<boolean> => {
        try {
            const orderExists = this.ordersHistoryIds.indexOf(orderId);
            if (orderExists !== -1) {
                if (toggle) {
                    this.ordersHistory[orderExists].itemsExpanded = !this.ordersHistory[orderExists].itemsExpanded;
                }

                if (this.ordersHistory[orderExists].itemsExpanded) {
                    this.ordersHistory[orderExists].itemsLoading = true;

                    const service = await app.service('order');
                    const order = (await service.get(orderId)) as IOrderGetApiResponse;

                    this.ordersHistory[orderExists].itemsEntries = order.items.map(item => ({
                        make: item.make,
                        model: item.model,
                        fromYear: item.fromYear,
                        toYear: item.toYear,
                        image: item.imageThumb || item.image || null,
                        id: item.itemId,
                        itemcode: item.itemCode,
                        customerItemcode: item.custItemCode,
                        itemname: item.itemName,
                        oem: item.oem1 || item.oem2 || null,
                        partsLink: item.partsLink1 || item.partsLink2 || null,
                        dpi: item.dpi,
                        certification: item.certification,
                        quantity: item.quantity,
                        price: unformat(item.price)
                    }));

                    this.ordersHistory[orderExists].itemsLoading = false;
                }
            }

            return true;
        } catch (error) {
            console.error(error);
            return error;
        }
    };

    @action
    public cancelOrReopenOrder = async (orderId: number, cancel: boolean = true) => {
        try {
            let orderFound = this.ordersHavingStatusChangedIds.indexOf(orderId);
            if (orderFound !== -1) {
                this.ordersStatusChangeStatuses[orderFound].status = EOrderStatusChangeStatus.Saving;
            } else {
                this.ordersStatusChangeStatuses.push({
                    orderId,
                    status: EOrderStatusChangeStatus.Saving
                });
            }

            const service = await app.service('order');
            const change = await service.patch(orderId, {
                status: cancel ? 0 : 1
            });

            this.ordersHistory.some(order => {
                if (order.id === orderId) {
                    order.status = cancel ? 0 : 1;
                    order.statusName = cancel ? 'Cancelled' : 'Reopened, request sent';
                    return true;
                }
            });

            orderFound = this.ordersHavingStatusChangedIds.indexOf(orderId);
            if (orderFound !== -1) {
                this.ordersStatusChangeStatuses[orderFound].status = EOrderStatusChangeStatus.Success;

                setTimeout(() => {
                    orderFound = this.ordersHavingStatusChangedIds.indexOf(orderId);
                    if (orderFound !== -1) {
                        this.ordersStatusChangeStatuses = [
                            ...this.ordersStatusChangeStatuses.slice(0, orderFound),
                            ...this.ordersStatusChangeStatuses.slice(orderFound + 1)
                        ];
                    }
                }, 1024);
            }

            return true;
        } catch (error) {
            const orderFound = this.ordersHavingStatusChangedIds.indexOf(orderId);
            if (orderFound !== -1) {
                this.ordersStatusChangeStatuses[orderFound].status = EOrderStatusChangeStatus.Error;
            }
            console.error(error);
            return error;
        }
    };

    @action
    private clearOrdersHistory = () => {
        this.ordersHistory = [];
        this.ordersHistoryTotal = 0;
        this.ordersHistoryPage = 1;
    };

    @action
    public openOrdersHistoryModal = () => {
        this.showOrdersHistoryModal = true;
        this.fetchOrdersHistory(true);
    };

    @action
    public hideOrdersHistoryModal = () => {
        this.showOrdersHistoryModal = false;
        this.clearOrdersHistory();
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Orders.ts